import { FunctionComponent } from "react";

import { faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";

import { useTranslation } from "react-i18next";

import { Icon } from "@/components";

import { draftSenderBlock, isSendableDraft } from "../utils/draftValidation.util";
import { Draft } from "../utils/inboxDraft.types";

import { SenderRecipientOption } from "./SenderRecipientDropdown";

/**
 * Says why Send is disabled for a draft's sender (PREC-4312): no real sender chosen yet, or one
 * that cannot send (a mailbox that needs a reconnect). It names the first draft Send would send
 * (`isSendableDraft`, the same predicate Send uses) whose sender blocks it — a follow-up's too —
 * so a draft whose default is still being decided and has nothing in it does not flash it.
 */
export const DraftSenderWarning: FunctionComponent<{
	drafts: Draft[];
	senderOptions: SenderRecipientOption[];
}> = ({ drafts, senderOptions }) => {
	const { t } = useTranslation();
	const block =
		drafts
			.filter(isSendableDraft)
			.map((draft) => draftSenderBlock(draft, senderOptions))
			.find((found) => !!found) ?? null;

	if (!block) {
		return null;
	}

	const reason =
		block.kind === "missing"
			? t("inbox.identity.chooseSender")
			: block.option.disabledReasonKey
				? t(block.option.disabledReasonKey)
				: block.option.disabledReason;

	return (
		<div
			className="flex items-center gap-2 px-3 py-2 bg-red-50 border-x border-border-light text-red-600"
			data-testid="draft-sender-warning"
		>
			<Icon className="w-3.5 h-3.5 shrink-0" icon={faTriangleExclamation} />
			<span className="text-xs">{reason}</span>
		</div>
	);
};
